/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: lots_notes_junction entity configuration
 * Generator: scripts/generate-api-clients/generate-api-clients.ts
 *
 * This file contains the API client for the lots_notes_junction table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the lots_notes_junction API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, lot_id, note_id, created_at, created_by, updated_at, trig_lot_address, trig_lot_number, trig_lot_status_name, trig_organization_id, trig_note_content, trig_note_title, trig_note_created_by, trig_note_is_internal, trig_note_type]
 * Searchable fields: []
 * Filterable fields: [id, lot_id, note_id, trig_organization_id]
 */

import { httpClient } from '../http-client.js';
import { globalSearchConfig } from '../../../config/search/global-search-config.js';
import { matchesAllTerms } from '../../../config/search/search-utils.js';
import type {
  LotsNotesJunctionRow,
  LotsNotesJunctionInsert,
  LotsNotesJunctionUpdate
} from '../generated_schemas/generated-lots_notes_junction-schema.js';
import { LotsNotesJunctionQueryOptions } from '../generated_schemas/generated-lots_notes_junction-schema.js';
export type { LotsNotesJunctionQueryOptions };

// Internal response type with non-null data (not exported to avoid conflicts with schema's Response type)
interface LotsNotesJunctionClientResponse {
  data: LotsNotesJunctionRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for lots_notes_junction table operations
 *
 * @description Provides type-safe access to lots_notes_junction API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class LotsNotesJunctionClient {
  private readonly basePath = '/lots-notes-junction';

  /**
   * Get all lots_notes_junction records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of lots_notes_junction records
   */
  async getAll(options?: LotsNotesJunctionQueryOptions): Promise<LotsNotesJunctionRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<LotsNotesJunctionClientResponse>(url);
    return response.data;
  }

  /**
   * Get a single lots_notes_junction record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the lots_notes_junction record
   */
  async getById(id: string): Promise<LotsNotesJunctionRow> {
    const response = await httpClient.get<{
      data: LotsNotesJunctionRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new lots_notes_junction record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created lots_notes_junction record
   */
  async create(data: LotsNotesJunctionInsert): Promise<LotsNotesJunctionRow> {
    const response = await httpClient.post<{
      data: LotsNotesJunctionRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing lots_notes_junction record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated lots_notes_junction record
   */
  async update(id: string, data: LotsNotesJunctionUpdate): Promise<LotsNotesJunctionRow> {
    const response = await httpClient.put<{
      data: LotsNotesJunctionRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a lots_notes_junction record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple lots_notes_junction records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created lots_notes_junction records
   */
  async createMany(data: LotsNotesJunctionInsert[]): Promise<LotsNotesJunctionRow[]> {
    const response = await httpClient.post<{
      data: LotsNotesJunctionRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Delete multiple lots_notes_junction records
   *
   * @param ids - Array of unique identifiers for records to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteMany(ids: string[]): Promise<void> {
    await httpClient.delete(`${this.basePath}/batch`, { ids });
  }

  /**
   * Search lots_notes_junction with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    query: string,
    columns?: string[],
    options?: LotsNotesJunctionQueryOptions
  ): Promise<LotsNotesJunctionClientResponse> {
    throw new Error('Search operations are not available for lots_notes_junction - no searchable fields configured');
  }

  /**
   * Get count of lots_notes_junction records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a lots_notes_junction record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'lots_notes_junction',
      publicFields: ['id', 'lot_id', 'note_id', 'created_at', 'created_by', 'updated_at', 'trig_lot_address', 'trig_lot_number', 'trig_lot_status_name', 'trig_organization_id', 'trig_note_content', 'trig_note_title', 'trig_note_created_by', 'trig_note_is_internal', 'trig_note_type'],
      searchableFields: [],
      filterableFields: ['id', 'lot_id', 'note_id', 'trig_organization_id'],
      sortableFields: ['created_at', 'updated_at'],
      readonlyFields: ['id', 'created_at', 'updated_at', 'trig_lot_address', 'trig_lot_number', 'trig_lot_status_name', 'trig_organization_id', 'trig_note_content', 'trig_note_title', 'trig_note_created_by', 'trig_note_is_internal', 'trig_note_type'],
      requiredFields: ['lot_id', 'note_id'],
    };
  }
}

// Export singleton instance for convenience
export const lotsNotesJunctionClient = new LotsNotesJunctionClient();
